























































































































import {
    defineComponent,
    computed,
    PropType,
    ref,
    watch,
} from '@vue/composition-api';

import { RouteName } from '@app/router/constants/RouteName';

import {
    SdsBrandImage,
    SdsIllustration,
    SdsButtonIcon,
} from '@superbet-group/web.lib.ui-components';

import SdsTabs from '@shared/components/tabs/SdsTabs.vue';
import type { TabItem } from '@shared/components/tabs/types';

import SdsBreadcrumbs from '@shared/components/breadcrumbs/SdsBreadcrumbs.vue';
import type { BreadcrumbItem } from '@shared/components/breadcrumbs/types';
import SdsPageHeaderBreadcrumbsActions from './SdsPageHeaderBreadcrumbsActions.vue';
import {
    useVueRouter,
    isMobileLayout,
} from '@superbet-group/web.lib.vue-utilities';

import SdsPageHeaderActions from './SdsPageHeaderActions.vue';
import type { PageHeaderActionItem } from './types';

export default defineComponent({
    name: 'SdsPageHeader',
    props: {
        name: {
            type: String,
            default: null,
        },
        subtitle: {
            type: String,
            default: null,
        },
        tabs: {
            type: Array as PropType<TabItem[]>,
            default: () => [],
        },
        defaultTabValue: {
            type: String,
            default: '',
        },
        activeTabValue: {
            type: String,
            default: '',
        },
        breadcrumbs: {
            type: Array as PropType<BreadcrumbItem[]>,
            default: () => [],
        },
        actions: {
            type: Array as PropType<PageHeaderActionItem[]>,
            default: () => [],
        },
        hasUnderline: {
            type: Boolean,
            default: true,
        },
        isFeatured: {
            type: Boolean,
            required: false,
            default: false,
        },
        onBackClick: {
            type: Function,
            required: false,
            default: undefined,
        },
        illustrationName: {
            type: String,
            required: false,
            default: '',
        },
        brandIllustrationName: {
            type: String,
            required: false,
            default: '',
        },
        shouldShowPageName: {
            type: Boolean,
            default: true,
        },
        actionsBreadcrumbs: {
            type: Array as PropType<PageHeaderActionItem[]>,
            default: () => [],
        },
    },
    emits: ['tabs:change'],
    // eslint-disable-next-line sonarjs/cognitive-complexity
    setup(props, { emit, slots }) {
        const { route } = useVueRouter();

        const hasSlot = (name: string) => !!slots[name];

        const hasBreadcrumbs = computed(() => props.breadcrumbs.length > 0 || hasSlot('breadcrumbs') || route.value.meta?.breadcrumbs?.length > 0);
        const hasActions = computed(() => props.actions.length > 0 || hasSlot('actions'));
        const hasActionsBreadcrumbs = computed(() => props.actionsBreadcrumbs.length > 0);
        const hasBackButton = computed(() => typeof props.onBackClick === 'function' && route.value.name !== RouteName.App.Home);
        const hasTabs = computed(() => props.tabs.length > 0 || hasSlot('tabs'));
        const isLastBreadcrumbInteractive = ref(true);

        const breadcrumbItems = ref(props.breadcrumbs.length > 0 ? props.breadcrumbs : route.value.meta?.breadcrumbs);
        const tabValue = ref(props.activeTabValue || props.defaultTabValue || props.tabs[0]?.value);

        // We're not showing page name & subtitle row on mobile, page name gets converted to breadcrumbs
        // If there are no breadcrumbs, we just don't show the title, per designs
        const hasPageNameRow = computed(() => !isMobileLayout() && props.shouldShowPageName);

        // @TODO: Where will we set translated page names if not provided via prop? In route meta?
        const pageName = computed(() => props.name ?? route.value.meta?.pageName);

        const hasPageNameIcon = computed(() => hasSlot('icon'));

        watch([() => props.breadcrumbs, () => route.value.meta?.breadcrumbs, pageName], () => {
            if (!hasBreadcrumbs.value) {
                breadcrumbItems.value = [];
            }

            breadcrumbItems.value = props.breadcrumbs.length > 0 ? props.breadcrumbs : route.value.meta?.breadcrumbs ?? [];

            // @TODO: Does every use case fit this behavior? Do we always convert this into breadcrumb?
            // If we're on mobile, current page gets converted to last breadcrumb item and page name & subtitle become hidden
            const pageAsBreadcrumb = {
                label: pageName.value,
            } as BreadcrumbItem;

            isLastBreadcrumbInteractive.value = false;

            breadcrumbItems.value = [...breadcrumbItems.value, pageAsBreadcrumb];
        }, { deep: true, immediate: true });

        // @TODO: Where will we set translated subtitle, if not provided via prop? In route meta?
        const pageSubtitle = computed(() => props.subtitle ?? route.value.meta?.subtitle);

        const illustrationTopPosition = computed(() => {
            if ((hasTabs.value && pageSubtitle.value) || (hasTabs.value && hasBreadcrumbs.value)) {
                return '12px';
            } else if (!hasTabs.value && pageSubtitle.value && hasBreadcrumbs.value) {
                return '8px';
            } return '-8px';
        });

        const handleTabChange = (value: string) => {
            tabValue.value = value;
            emit('tabs:change', value);
        };

        // In case of external changes
        watch(() => props.activeTabValue, (newValue) => {
            tabValue.value = newValue;
        });

        return {
            isMobile: computed(() => isMobileLayout()),
            handleTabChange,
            tabValue,
            hasBreadcrumbs,
            hasActions,
            hasActionsBreadcrumbs,
            hasBackButton,
            hasPageNameRow,
            hasPageNameIcon,
            hasTabs,
            breadcrumbItems,
            pageName,
            pageSubtitle,
            isLastBreadcrumbInteractive,
            illustrationTopPosition,
        };
    },
    components: {
        SdsBreadcrumbs,
        SdsPageHeaderActions,
        SdsPageHeaderBreadcrumbsActions,
        SdsTabs,
        SdsIllustration,
        SdsBrandImage,
        SdsButtonIcon,
    },
});
